import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { AuthContext, ToastContext, Spinner } from '@enter/core';
import { ProviderEvidence } from '@enter/core';
import { IInjectedRootStore } from 'stores';
import { DialogContext } from 'services';
import { EvidenceHeader, EvidenceInfoCards, EvidenceViewSection } from './components';
import * as S from './evidence-details.styles';

const Loading = React.memo(() => (
    <S.LoadingContainer>
        <Spinner />
    </S.LoadingContainer>
));

type IInjectedProps = IInjectedRootStore;

export const EvidenceDetailsOutlet = inject('rootStore')(
    observer((props: {}) => {
        const { rootStore } = props as IInjectedProps;
        const { evidenceId } = useParams<{ evidenceId: string }>();
        const navigate = useNavigate();
        
        if (!rootStore || !evidenceId) {
            return <></>;
        }

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        
        const [evidence, setEvidence] = useState<ProviderEvidence | undefined>();
        const [loading, setLoading] = useState(true);
        const [isNavigating, setIsNavigating] = useState(false);
        
        const paginationState = rootStore.evidenceStore.paginationState;

        const handleFetchEvidence = useCallback(async () => {
            if (!evidenceId) return;
            setLoading(true);
            try {
                authContext.setGroup?.('initEvidence');
                const fetchedEvidence = await rootStore.evidenceStore.getEvidence(evidenceId);
                setEvidence(fetchedEvidence);
            } catch (error) {
                setEvidence(undefined);
                // Error handling is done by core, no need for toast here
            } finally {
                setLoading(false);
                authContext.cancelGroup?.('initEvidence');
            }
        }, [evidenceId, rootStore, authContext]);

        const copyToClipboard = (text: string, successMessage: string) => {
            navigator.clipboard.writeText(text).then(() => {
                toastContext.notify?.(successMessage, 'right');
            }).catch(() => {
                toastContext.notify?.('Failed to copy to clipboard', 'right');
            });
        };

        const copyEvidenceId = () => {
            if (evidenceId) {
                copyToClipboard(evidenceId, 'Evidence ID copied to clipboard');
            }
        };

        const copyEvidenceUrl = () => {
            const currentUrl = window.location.href;
            copyToClipboard(currentUrl, 'Evidence URL copied to clipboard');
        };

        const handleNavigateToOffset = useCallback(async (newOffset: number) => {
            if (!paginationState) return;
            
            setIsNavigating(true);
            try {
                const result = await rootStore.evidenceStore.fetchOneByOffset(
                    newOffset,
                    paginationState.filters,
                    paginationState.sortRequest,
                );
                
                if (result.evidence?.id) {
                    rootStore.evidenceStore.updatePaginationOffset(newOffset, result.total);
                    navigate(`../${result.evidence.id}`, { relative: 'path' });
                }
            } catch (error) {
                // Error handling is done by core, no need for toast here
            } finally {
                setIsNavigating(false);
            }
        }, [paginationState, rootStore.evidenceStore, navigate]);

        const handlePrevious = useCallback(() => {
            if (paginationState && paginationState.offset > 0) {
                handleNavigateToOffset(paginationState.offset - 1);
            }
        }, [paginationState, handleNavigateToOffset]);

        const handleNext = useCallback(() => {
            if (paginationState && paginationState.offset < paginationState.total - 1) {
                handleNavigateToOffset(paginationState.offset + 1);
            }
        }, [paginationState, handleNavigateToOffset]);

        useEffect(() => {
            handleFetchEvidence();
        }, [handleFetchEvidence]);

        useEffect(() => {
            document.title = 'ENTER Platform - Evidence Details';
        }, [evidence]);

        if (loading || !evidence) {
            return <Loading />;
        }

        return (
            <S.Wrapper>
                <EvidenceHeader
                    evidence={evidence}
                    onCopyEvidenceId={copyEvidenceId}
                    onCopyEvidenceUrl={copyEvidenceUrl}
                    paginationOffset={paginationState?.offset}
                    paginationTotal={paginationState?.total}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    isNavigating={isNavigating}
                />

                {/* Evidence Information Cards */}
                <EvidenceInfoCards
                    evidence={evidence}
                    dialogContext={dialogContext}
                />

                {/* Evidence View Section (View Only) */}
                <div style={{ marginTop: '20px', width: '100%' }}>
                    <EvidenceViewSection
                        evidence={evidence}
                    />
                </div>
            </S.Wrapper>
        );
    }),
);

